import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { ScrollArea } from '../components/ScrollArea';
import { Button } from '../components/ui/button';
import {
  Bookmark, Play, Trash2, Download, Clock, Database, Search, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

export default function SavedQueries() {
  const [queries, setQueries] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rerunResult, setRerunResult] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadQueries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/queries`, authH());
      setQueries(data);
    } catch {}
  }, []);

  useEffect(() => { loadQueries(); }, [loadQueries]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/queries/${id}`, authH());
      setQueries(q => q.filter(x => x.id !== id));
      if (selected?.id === id) { setSelected(null); setRerunResult(null); }
      toast.success('Query deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleRerun = async (queryId) => {
    setRerunning(true);
    setRerunResult(null);
    try {
      const { data } = await axios.post(`${API}/queries/${queryId}/run`, {}, authH());
      setRerunResult(data);
      toast.success('Query re-executed');
    } catch { toast.error('Failed to re-run query'); }
    setRerunning(false);
  };

  const handleExportCSV = (results) => {
    if (!results?.columns?.length) return;
    const header = results.columns.join(',');
    const rows = results.rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `query_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await axios.get(`${API}/queries/${id}`, authH());
      setSelected(data);
      setRerunResult(null);
    } catch { toast.error('Failed to load query'); }
  };

  const filtered = queries.filter(q =>
    !searchTerm || q.name.toLowerCase().includes(searchTerm.toLowerCase()) || q.query_text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayResults = rerunResult?.results || selected?.results_snapshot;

  return (
    <DashboardLayout>
      <div data-testid="saved-queries-page" className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>Saved Queries</h2>
            <p className="text-xs text-[#9ca3af] mt-0.5">{queries.length} saved quer{queries.length === 1 ? 'y' : 'ies'}</p>
          </div>
          <div className="flex items-center gap-1.5 bg-white border border-[#e6e8f2] rounded-lg px-3 py-1.5">
            <Search size={13} className="text-[#9ca3af]" />
            <input data-testid="query-search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search queries..." className="text-sm bg-transparent outline-none w-40" />
          </div>
        </div>

        <div className="flex gap-6">
          {/* Query List */}
          <div className="w-full lg:w-1/3 space-y-2">
            {filtered.length === 0 ? (
              <div className="bg-white border border-[#e6e8f2] rounded-xl py-12 text-center">
                <Bookmark size={28} className="text-[#d4d8e6] mx-auto mb-2" />
                <p className="text-sm text-[#9ca3af]">{queries.length === 0 ? 'No saved queries yet. Save a query from the Chat page.' : 'No queries match your search.'}</p>
              </div>
            ) : (
              filtered.map(q => (
                <div key={q.id} data-testid={`query-card-${q.id}`}
                  onClick={() => loadDetail(q.id)}
                  className={`bg-white border rounded-xl p-3 hover:shadow-md transition-all cursor-pointer group ${selected?.id === q.id ? 'border-[#5b63ea] ring-1 ring-[#5b63ea]/20' : 'border-[#e6e8f2]'}`}
                >
                  <div className="flex items-start gap-2">
                    <Bookmark size={14} className="text-[#5b63ea] mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1e1e2e] truncate">{q.name}</p>
                      <p className="text-[10px] text-[#9ca3af] mt-0.5 truncate">{q.query_text}</p>
                      <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#9ca3af]">
                        <span className="flex items-center gap-0.5"><Clock size={9} /> {q.created_at?.split('T')[0]}</span>
                        {q.last_run && <span>Last run: {q.last_run.split('T')[0]}</span>}
                      </div>
                    </div>
                    <button onClick={(e) => handleDelete(e, q.id)} className="opacity-0 group-hover:opacity-100 text-[#9ca3af] hover:text-red-500 transition-all">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Detail Panel */}
          <div className="hidden lg:block flex-1">
            {selected ? (
              <div className="bg-white border border-[#e6e8f2] rounded-xl p-5 space-y-4" data-testid="query-detail-panel">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>{selected.name}</h3>
                  <div className="flex items-center gap-2">
                    <Button data-testid="btn-rerun" onClick={() => handleRerun(selected.id)} disabled={rerunning}
                      className="bg-[#5b63ea] hover:bg-[#4a52d9] text-white gap-1 h-8 text-xs">
                      <Play size={12} /> {rerunning ? 'Running...' : 'Re-run'}
                    </Button>
                    {displayResults?.columns?.length > 0 && (
                      <Button variant="ghost" size="sm" onClick={() => handleExportCSV(displayResults)} className="h-8 text-xs gap-1" data-testid="btn-export-query-csv">
                        <Download size={12} /> CSV
                      </Button>
                    )}
                  </div>
                </div>
                {selected.description && <p className="text-xs text-[#6b7280]">{selected.description}</p>}
                <div className="bg-[#1e1e2e] rounded-lg px-4 py-3">
                  <pre className="text-[#e2e8f0] text-xs whitespace-pre-wrap" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{selected.query_text}</pre>
                </div>

                {/* Results */}
                {rerunResult && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-[#1e1e2e]">{rerunResult.answer}</p>
                    {rerunResult.sql_display && (
                      <div className="bg-[#f8f9fc] border border-[#e6e8f2] rounded-lg p-2">
                        <pre className="text-xs text-[#4b5563]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{rerunResult.sql_display}</pre>
                      </div>
                    )}
                  </div>
                )}

                {displayResults?.columns?.length > 0 && (
                  <div className="border border-[#e6e8f2] rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-[#f8f9fc] border-b border-[#e6e8f2]">
                      <span className="text-xs text-[#6b7280] font-medium">{displayResults.row_count} result{displayResults.row_count !== 1 ? 's' : ''}</span>
                    </div>
                    <ScrollArea className="overflow-x-auto overflow-y-auto max-h-[300px]">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-[#f8f9fc]">
                          <tr>
                            {displayResults.columns.map((col, i) => (
                              <th key={i} className="px-3 py-2 text-left font-semibold text-[#1e1e2e] border-b border-[#e6e8f2]">{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {displayResults.rows.map((row, ri) => (
                            <tr key={ri} className="hover:bg-[#f5f6fb] border-b border-[#e6e8f2] last:border-0">
                              {row.map((val, ci) => (
                                <td key={ci} className="px-3 py-1.5 text-[#4b5563]">{val ?? '—'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white border border-[#e6e8f2] rounded-xl py-16 text-center">
                <FileText size={32} className="text-[#d4d8e6] mx-auto mb-2" />
                <p className="text-sm text-[#9ca3af]">Select a query to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
